import { InjectionToken } from '@angular/core';

import { AvTelemetryEnvelope } from './av-telemetry.model';

/** Deliberately no packageGuid: no package identifiers are held at rest on the user's machine. */
export interface AvTelemetryBufferRow {
  /** Global insertion order, which `seq` is not — that resets per session. */
  id?: number;
  /** The device that WROTE the row, not necessarily the one shipping it. */
  deviceCode: string;
  expiresAt: number;
  /** Serialized size, stamped at write time so the cap can be enforced on bytes. */
  bytes: number;
  envelope: AvTelemetryEnvelope;
}

export interface AvTelemetryMetricAggregate {
  avg: number;
  min: number;
  max: number;
}

export interface AvTelemetryBufferConfig {
  ttlMs: number;
  maxBytes: number;
  flushIntervalMs: number;
  collapseWindowMs: number;
}

/**
 * Rough per-row size, used only to decide when a precise byte total is worth computing.
 * IndexedDB cannot aggregate, so the alternative is reading every row on every flush.
 */
export const AV_TELEMETRY_ESTIMATED_ROW_BYTES = 3_500;

export const AV_TELEMETRY_BUFFER_DEFAULTS: AvTelemetryBufferConfig = {
  // A signing cannot be rejoined from a previous day's schedule, so older data can never ship.
  ttlMs: 24 * 60 * 60 * 1_000,
  // Capped on bytes, not rows: many signers are on mobile and a raw tick is ~3.5 KB, so a row
  // count says little about footprint. 10 MB is ~2h of buffering at the observed ~24 ticks/min.
  maxBytes: 10 * 1024 * 1024,
  flushIntervalMs: 60_000,
  collapseWindowMs: 10_000,
};

export const AV_TELEMETRY_BUFFER_CONFIG = new InjectionToken<AvTelemetryBufferConfig>(
  'AV_TELEMETRY_BUFFER_CONFIG',
  { factory: () => AV_TELEMETRY_BUFFER_DEFAULTS }
);
